/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Calendar, Euro, ChevronLeft, ChevronRight,
  CheckCircle2, Filter, TrendingUp, ClipboardCheck, Building2,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ClinicalRecordPanel } from '@/components/clinical/ClinicalRecordPanel';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { StripeConnectCard } from '@/components/telemedicine/StripeConnectCard';
import KycStripeCard from '@/components/telemedicine/KycStripeCard';
import { OnboardingBanner } from '@/components/OnboardingCTA';
import { AvailabilityDialog } from '@/components/telemedicine/doctor/AvailabilityDialog';
import { DoctorProfileCard } from '@/components/telemedicine/doctor/DoctorProfileCard';
import { TimelineAppointment, KpiCard } from '@/components/telemedicine/doctor/AgendaWidgets';
import { URGENCY_META, dayLabel, startOfDay } from '@/components/telemedicine/doctor/doctorConstants';
import {
  getMyDoctorProfile, createSubscriptionCheckout,
  listMyAppointments,
} from '@/lib/telemedicine';

/**
 * Doctor Command Center — split-screen workspace:
 *  - LEFT  (resizable): agenda dinamica del giorno con indicatore "adesso",
 *    filtro urgenza e join 1-click.
 *  - RIGHT (resizable): Cartella clinica (dettatura → SOAP AI locale → salva
 *    offline) oppure tab "Studio" (profilo, disponibilità, Stripe).
 *  Su mobile i due pannelli diventano tab per ridurre lo scroll.
 *
 * Refactor note (Code Quality): AvailabilityDialog, DoctorProfileCard,
 * TimelineAppointment and KpiCard were extracted to
 * `components/telemedicine/doctor/*` to keep this page under ~300 lines.
 */

// ---------- Main Command Center ----------
const DoctorCommandCenter = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'it';
  const navigate = useNavigate();
  const { user } = useAuth();

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [subCheckingOut, setSubCheckingOut] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [dayOffset, setDayOffset] = useState(0);
  const [selected, setSelected] = useState(null);
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [availOpen, setAvailOpen] = useState(false);
  // `now` is a self-refreshing timestamp used by nowAppt/nextAppt useMemos.
  // Using a state ticker (instead of a bare `tick` counter) removes the
  // exhaustive-deps warning cleanly.
  const [now, setNow] = useState(() => Date.now());

  const day = useMemo(() => {
    const d = new Date();
    d.setHours(0,0,0,0);
    d.setDate(d.getDate() + dayOffset);
    return d;
  }, [dayOffset]);

  const loadProfile = React.useCallback(() => {
    setProfileLoading(true);
    getMyDoctorProfile()
      .then(setProfile)
      .catch((err) => {
        if (process.env.NODE_ENV !== 'production') console.debug('[DoctorCC] loadProfile failed', err);
      })
      .finally(() => setProfileLoading(false));
  }, []);
  const loadAppts = React.useCallback(() => {
    listMyAppointments('doctor')
      .then((res) => setAppointments(res.appointments || []))
      .catch((err) => {
        if (process.env.NODE_ENV !== 'production') console.debug('[DoctorCC] loadAppts failed', err);
      });
  }, []);

  useEffect(() => {
    loadProfile();
    loadAppts();
  }, [loadProfile, loadAppts]);

  // Refresh "now" indicator every 30s
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(iv);
  }, []);

  const startSub = async () => {
    setSubCheckingOut(true);
    try {
      const { url } = await createSubscriptionCheckout(window.location.origin);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('genericError'));
      setSubCheckingOut(false);
    }
  };

  const dayAppointments = useMemo(() => {
    const start = day.getTime();
    const end = start + 24 * 60 * 60000;
    return appointments
      .filter((a) => {
        const t2 = new Date(a.scheduled_at).getTime();
        if (isNaN(t2) || t2 < start || t2 >= end) return false;
        if (urgencyFilter !== 'all' && (a.urgency || 'routine') !== urgencyFilter) return false;
        if (a.status === 'cancelled') return false;
        return true;
      })
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  }, [appointments, day, urgencyFilter]);

  const nowAppt = useMemo(() => {
    const slotMs = (profile?.slot_minutes || 30) * 60000;
    return dayAppointments.find((a) => {
      const dt = new Date(a.scheduled_at).getTime();
      return dt <= now && (dt + slotMs) >= now;
    });
  }, [dayAppointments, profile?.slot_minutes, now]);

  const nextAppt = useMemo(() => {
    return dayAppointments.find((a) => new Date(a.scheduled_at).getTime() > now);
  }, [dayAppointments, now]);

  // KPIs
  const todaysAppts = useMemo(() => appointments.filter((a) => startOfDay(new Date(a.scheduled_at)).getTime() === startOfDay(new Date()).getTime() && a.status !== 'cancelled'), [appointments]);
  const upcomingWeek = useMemo(() => appointments.filter((a) => {
    const dt = new Date(a.scheduled_at).getTime();
    const t0 = Date.now();
    return dt >= t0 && dt <= t0 + 7 * 24 * 60 * 60000 && a.status !== 'cancelled';
  }), [appointments]);
  const completedAll = useMemo(() => appointments.filter((a) => a.status === 'completed').length, [appointments]);

  const activeAppt = selected || nowAppt || nextAppt || null;
  const openRoom = (id) => navigate(`/telemedicine/room/${id}`);

  const AgendaPane = (
    <div className="h-full flex flex-col" data-testid="agenda-pane">
      <div className="px-3 py-2.5 border-b border-border/70 bg-card/60 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" size="icon" onClick={() => setDayOffset(dayOffset - 1)} data-testid="day-prev-btn" aria-label="Giorno precedente">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <button type="button" onClick={() => setDayOffset(0)} className="text-center rounded-lg px-2 py-0.5 hover:bg-muted transition-colors" data-testid="day-today-btn">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{t('doctorAgendaTitle')}{dayOffset !== 0 && ' · torna a oggi'}</div>
            <div className="font-display font-bold text-sm capitalize">{dayLabel(day, locale)}</div>
          </button>
          <Button variant="ghost" size="icon" onClick={() => setDayOffset(dayOffset + 1)} data-testid="day-next-btn" aria-label="Giorno successivo">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin">
          <Filter className="h-3 w-3 text-muted-foreground flex-shrink-0" />
          {['all', 'routine', 'urgent', 'emergency'].map((u) => (
            <button
              key={u}
              onClick={() => setUrgencyFilter(u)}
              className={cn(
                'flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase transition-colors border',
                urgencyFilter === u ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border/60 text-muted-foreground hover:border-primary/40'
              )}
              data-testid={`urgency-filter-${u}`}
            >
              {u === 'all' ? t('doctorAllUrgencies') : t(URGENCY_META[u].label)}
            </button>
          ))}
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="relative p-3 pl-4">
          <div className="absolute left-4 top-3 bottom-3 w-px bg-border" />
          <div className="space-y-3">
            {dayAppointments.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-6 text-center ml-2" data-testid="doctor-no-appts">
                <Calendar className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">{t('doctorNoApptsToday')}</p>
              </div>
            ) : (
              dayAppointments.map((a) => (
                <TimelineAppointment
                  key={a.id}
                  appt={a}
                  isNow={nowAppt?.id === a.id}
                  isNext={nextAppt?.id === a.id}
                  onOpenRoom={openRoom}
                  selected={activeAppt?.id === a.id}
                  onSelect={setSelected}
                />
              ))
            )}
          </div>
        </div>
      </ScrollArea>
    </div>
  );

  const WorkspacePane = (
    <Tabs defaultValue="record" className="h-full flex flex-col" data-testid="workspace-pane">
      <div className="px-3 pt-2 border-b border-border/70 bg-card/60">
        <TabsList className="h-9 rounded-lg">
          <TabsTrigger value="record" className="text-xs rounded-md" data-testid="tab-record"><ClipboardCheck className="h-3.5 w-3.5 mr-1" /> Cartella</TabsTrigger>
          <TabsTrigger value="studio" className="text-xs rounded-md" data-testid="tab-studio"><Building2 className="h-3.5 w-3.5 mr-1" /> Studio</TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="record" className="flex-1 m-0 min-h-0">
        <ClinicalRecordPanel appt={activeAppt} onOpenRoom={openRoom} authorId={user?.id} />
      </TabsContent>
      <TabsContent value="studio" className="flex-1 m-0 min-h-0">
        <ScrollArea className="h-full">
          <div className="p-4 space-y-4">
            {profileLoading ? <Skeleton className="h-72 rounded-xl" /> : (
              <>
                <DoctorProfileCard
                  profile={profile}
                  onSaved={loadProfile}
                  onStartSubscription={startSub}
                  subCheckingOut={subCheckingOut}
                  onOpenAvailability={() => setAvailOpen(true)}
                />
                <StripeConnectCard />
              </>
            )}
          </div>
        </ScrollArea>
      </TabsContent>
    </Tabs>
  );

  return (
    <div className="space-y-4" data-testid="doctor-command-center">
      <OnboardingBanner />
      <KycStripeCard embedded={true} />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label={t('doctorKpiToday')} value={todaysAppts.length} icon={Calendar} />
        <KpiCard label={t('doctorKpiWeek')} value={upcomingWeek.length} icon={TrendingUp} color="accent" />
        <KpiCard label={t('doctorKpiCompleted')} value={completedAll} icon={CheckCircle2} color="accent" />
        <KpiCard label={t('doctorKpiVisitPrice')} value={`€ ${(profile?.visit_price_eur || 25).toFixed(0)}`} icon={Euro} />
      </div>

      {/* Split-screen (desktop) */}
      <div className="hidden lg:block rounded-xl border border-border/70 bg-background overflow-hidden" style={{ height: 'calc(100vh - 260px)', minHeight: 560 }}>
        <ResizablePanelGroup direction="horizontal" autoSaveId="doctor-cc-split">
          <ResizablePanel defaultSize={34} minSize={24} maxSize={50}>{AgendaPane}</ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={66} minSize={40}>{WorkspacePane}</ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* Stacked tabs (mobile / tablet) */}
      <div className="lg:hidden rounded-xl border border-border/70 bg-background overflow-hidden" data-testid="doctor-cc-mobile">
        <Tabs defaultValue="agenda">
          <TabsList className="w-full grid grid-cols-2 rounded-none h-11">
            <TabsTrigger value="agenda" className="touch-target" data-testid="mobile-tab-agenda"><Calendar className="h-4 w-4 mr-1" /> Agenda</TabsTrigger>
            <TabsTrigger value="work" className="touch-target" data-testid="mobile-tab-work"><ClipboardCheck className="h-4 w-4 mr-1" /> Cartella</TabsTrigger>
          </TabsList>
          <TabsContent value="agenda" className="m-0 h-[70vh]">{AgendaPane}</TabsContent>
          <TabsContent value="work" className="m-0 h-[75vh]">{WorkspacePane}</TabsContent>
        </Tabs>
      </div>

      <AvailabilityDialog open={availOpen} onOpenChange={setAvailOpen} onSaved={() => {}} />
    </div>
  );
};

export default DoctorCommandCenter;
